<template>
  <div class="pricing-view">
    <div class="container">
      <!-- Loading State -->
      <div v-if="!userData" class="loading-container">
        <PageHeader title="Billing & Licenties" description="Beheer je subscription en modules" />
        <div class="loading-state">Laden...</div>
      </div>

      <!-- New Stripe Billing Interface -->
      <div v-else-if="hasStripeSubscription || showNewInterface">
        <PageHeader title="Billing & Licenties" description="Beheer je subscription en modules" />

        <!-- Success Banner -->
        <div v-if="showSuccessMessage" class="success-banner">
          <div class="success-banner__content">
            <BaseIcon name="check-circle-open" size="20" />
            <span>Betaling succesvol! Je subscription wordt nu geactiveerd.</span>
          </div>
        </div>

        <!-- Error Banner -->
        <div v-if="error" class="error-banner">
          {{ error }}
        </div>

        <!-- Current Subscription -->
        <section v-if="hasBaseLicense" class="current-subscription">
          <div class="plan-card plan-card--active">
            <div class="plan-card__header">
              <div>
                <h3>{{ userData.base_license?.name || baseLicenseSubscription?.license?.name || 'Base License' }}</h3>
                <span class="badge badge--active">Actief</span>
              </div>
            </div>

            <div class="plan-card__details">
              <p class="plan-card__price">
                €{{ baseLicensePrice }}/maand
              </p>
              <p class="plan-card__users">
                {{ userData.base_license.quantity }} gebruiker(s) van {{ userData.base_license.max_users }}
              </p>

              <div v-if="activeModuleSubscriptions.length > 0" class="modules-list">
                <h4>Actieve Modules:</h4>
                <ul>
                  <li v-for="subscription in activeModuleSubscriptions" :key="subscription.id">
                    <div class="modules-list__item">
                      <BaseIcon name="check" size="12" />
                      <strong>{{ subscription.license?.name || subscription.name }}</strong>
                      <span class="modules-list__price">- €{{ subscription.license?.price || 0 }}/maand</span>
                    </div>
                  </li>
                </ul>
              </div>

              <div class="plan-card__total">
                <strong>Totaal: €{{ totalMonthly }}/maand</strong>
              </div>
            </div>

            <div class="plan-card__actions">
              <Btn
                variant="primary"
                :loading="portalLoading"
                @click="handleOpenPortal"
              >
                Beheer Billing
              </Btn>
            </div>
          </div>
        </section>

        <!-- No Subscription -->
        <section v-else class="no-subscription">
          <h2>Geen actieve subscription</h2>
          <p>Kies een plan om te beginnen:</p>
        </section>

        <!-- Base License Selection - Show all available base licenses -->
        <section v-if="!hasBaseLicense" class="base-license-selection">
          <h2>Kies je Basis Licentie</h2>
          <div v-if="loading" class="loading-state">Laden...</div>
          <div v-else-if="availableBaseLicenses.length === 0" class="no-licenses">
            <p>Geen beschikbare licenses gevonden.</p>
          </div>
          <div v-else class="pricing-table pricing-table--grid">
            <div
              v-for="license in availableBaseLicenses"
              :key="license.id"
              class="pricing-card"
            >
              <!-- License Header -->
              <div class="pricing-card__header">
                <h3>{{ license.name }}</h3>
                <div class="pricing-card__price">
                  <span class="currency">€</span>
                  <span class="amount">{{ license.price }}</span>
                  <span class="period">/maand</span>
                </div>
                <div v-if="license.min_users && license.max_users" class="pricing-card__users-info">
                  Voor {{ license.min_users }}-{{ license.max_users }} gebruikers
                </div>
                <Btn
                  variant="primary"
                  class="pricing-card__cta"
                  :loading="checkoutLoading && selectedLicenseId === license.id"
                  @click="handleCheckoutForLicense(license)"
                >
                  Kies Plan
                </Btn>
              </div>

              <!-- License Features -->
              <div class="pricing-card__features">
                <div class="feature-category">
                  <h4>Inclusief</h4>
                  <ul class="feature-list">
                    <li
                      v-for="(feature, index) in getLicenseFeatures(license)"
                      :key="index"
                      class="feature-item"
                    >
                      <div class="feature-category__icon">
                        <BaseIcon name="check" size="12" />
                      </div>
                      <span>{{ feature }}</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- Modules Section -->
        <section class="modules-section">
          <h2>Modules {{ hasBaseLicense ? '(optioneel)' : '' }}</h2>
          <div v-if="loading" class="loading-state">Laden...</div>
          <div v-else class="modules-grid">
            <div
              v-for="module in availableModules"
              :key="module.id"
              :class="['module-card', { 'module-card--active': isModuleActive(module.id) }]"
            >
              <div class="module-card__header">
                <h3>{{ module.name }}</h3>
                <span v-if="isModuleActive(module.id)" class="badge">✓ Actief</span>
              </div>

              <p class="module-card__price">€{{ module.price }}/maand</p>
              <p v-if="module.description" class="module-card__description">
                {{ module.description }}
              </p>

              <div v-if="isModuleActive(module.id)" class="module-card__info">
                Gebruik "Beheer Billing" om deze module te verwijderen
              </div>
              <Btn
                v-else
                variant="secondary"
                :disabled="!hasBaseLicense || addModuleLoading"
                :loading="addModuleLoading === module.id"
                @click="hasBaseLicense ? handleAddModule(module.id) : scrollToLicenses()"
              >
                {{ hasBaseLicense ? `Toevoegen - €${module.price}/maand` : 'Koop eerst Funnelflow Team' }}
              </Btn>
            </div>
          </div>
        </section>
      </div>

      <!-- Old Static Pricing View (Backward Compatibility) -->
      <div v-else>
        <PageHeader title="Pricing Plans" description="Choose the plan that best fits your needs" />
        <div class="billing-toggle" style="display: none">
          <span :class="{ active: !isMonthly }">Yearly</span>
          <ToggleInput
            id="billing-period"
            label=""
            :modelValue="isMonthly"
            @update:modelValue="isMonthly = $event"
          />
          <span :class="{ active: isMonthly }">Monthly</span>
          <span class="savings-badge" v-if="!isMonthly">Save 20%</span>
        </div>
        <div class="pricing-table" style="display: none">
          <div v-for="tier in displayTiers" :key="tier.name" class="pricing-card">
            <div class="pricing-card__header">
              <h3>{{ tier.name }}</h3>
              <div class="pricing-card__price">
                <span class="currency">€</span>
                <span class="amount">{{ tier.displayPrice }}</span>
                <span class="period">/{{ isMonthly ? 'month' : 'year' }}</span>
              </div>
              <div v-if="tier.duration" class="pricing-card__duration">
                {{ tier.duration }}
              </div>
              <Btn variant="primary" class="pricing-card__cta">
                  Kies Plan
              </Btn>
            </div>

            <div class="pricing-card__features">
              <template v-for="(category, key) in tier.categories" :key="key">
                <div class="feature-category">
                  <h4>{{ category.name }}</h4>
                  <ul>
                    <li v-for="feature in category.features" :key="feature">
                      <div class="feature-category__icon">
                        <BaseIcon name="check" size="12" />
                      </div>
                      {{ feature }}
                    </li>
                  </ul>
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { ref, computed, onMounted } from 'vue';
  import { useRoute, useRouter } from 'vue-router';
  import { useUserStore } from '@/store/userStore';
  import { useUIStore } from '@/store/uiStore';
  import { licenseService } from '@/services/licenseService';
  import PageHeader from '@/components/partials/PageHeader.vue';
  import Btn from '@/components/partials/Btn.vue';
  import BaseIcon from '@shared/components/BaseIcon.vue';
  import ToggleInput from '@/components/inputs/ToggleInput.vue';

  export default {
    name: 'SettingsBillingView',

    components: {
      PageHeader,
      Btn,
      BaseIcon,
      ToggleInput,
    },

    setup() {
      const userStore = useUserStore();
      const uiStore = useUIStore();
      const route = useRoute();
      const router = useRouter();

      // State
      const loading = ref(false);
      const checkoutLoading = ref(false);
      const portalLoading = ref(false);
      const addModuleLoading = ref(null);
      const error = ref(null);
      const licenses = ref({ base: [], modules: [] });
      const selectedQuantity = ref(3);
      const selectedLicenseId = ref(null);
      const isMonthly = ref(false);
      const showSuccessMessage = ref(false);

      // Computed
      const userData = computed(() => userStore.userData);
      const hasBaseLicense = computed(() => {
        return userData.value?.base_license !== null && userData.value?.base_license !== undefined;
      });
      const hasStripeSubscription = computed(() => {
        return hasBaseLicense.value;
      });
      const showNewInterface = computed(() => {
        // Always show new interface if userData exists
        // The backend should always return base_license (even if null) for the new structure
        // If userData exists, we assume it's the new structure
        return !!userData.value;
      });

      const activeModules = computed(() => {
        return userData.value?.active_modules || [];
      });

      const activeModuleSubscriptions = computed(() => {
        if (userData.value?.license_subscriptions) {
          return userData.value.license_subscriptions.filter(
            (sub) => sub.license?.type === 'module' && sub.status === 'active'
          );
        }
        // Fallback to active_modules array
        return activeModules.value.map((module) => ({
          id: module.id,
          name: module.name,
          license: {
            name: module.name,
            price: getModulePrice(module.id),
          },
        }));
      });

      const baseLicense = computed(() => {
        // Find base license, excluding free/trial plans
        const allBase = licenses.value.base || [];
        return allBase.find(
          (l) =>
            (l.name === 'Funnelflow Team' || l.name === 'Smartflow Team') &&
            l.price > 0 &&
            !l.name.toLowerCase().includes('free') &&
            !l.name.toLowerCase().includes('trial')
        );
      });

      const availableModules = computed(() => {
        return licenses.value.modules || [];
      });

      const availableBaseLicenses = computed(() => {
        // Filter out free plans (price === 0) and plans with "free" in the name
        const allLicenses = licenses.value.base || [];
        return allLicenses.filter(
          (license) =>
            license.price > 0 &&
            !license.name.toLowerCase().includes('free') &&
            !license.name.toLowerCase().includes('trial')
        );
      });

      const baseLicenseSubscription = computed(() => {
        if (!userData.value?.license_subscriptions) return null;
        return userData.value.license_subscriptions.find(
          (sub) => sub.license?.type === 'base' && sub.status === 'active'
        );
      });

      const baseLicensePrice = computed(() => {
        if (baseLicenseSubscription.value?.license?.price) {
          return baseLicenseSubscription.value.license.price;
        }
        // Fallback to baseLicense from API if available
        if (baseLicense.value) {
          return baseLicense.value.price;
        }
        return 0;
      });

      const totalMonthly = computed(() => {
        let total = baseLicensePrice.value;
        if (userData.value?.license_subscriptions) {
          userData.value.license_subscriptions.forEach((sub) => {
            if (sub.license?.type === 'module' && sub.status === 'active') {
              total += sub.license.price || 0;
            }
          });
        }
        return total;
      });

      const maxUsers = computed(() => {
        if (!baseLicense.value) return 10;
        return baseLicense.value.max_users || 10;
      });

      const userRange = computed(() => {
        if (!baseLicense.value) return Array.from({ length: 10 }, (_, i) => i + 1);
        const min = baseLicense.value.min_users || 1;
        const max = baseLicense.value.max_users || 10;
        return Array.from({ length: max - min + 1 }, (_, i) => min + i);
      });

      // Old pricing tiers (backward compatibility - NO FREE PLAN)
      const baseTiers = ref([
        {
          name: 'Starter',
          price: 49,
          categories: {
            formBuilding: {
              name: 'Form Building',
              features: [
                'Unlimited forms',
                'Multi-page forms',
                '50+ field types',
                'Basic field validation',
                'File upload fields',
                'Date & time fields',
                'Conditional logic',
                'Form templates',
                'Duplicate forms',
              ],
            },
            usage: {
              name: 'Usage',
              features: [
                '150 responses/month',
                'Unlimited users',
                'Basic form analytics',
                'CSV exports',
              ],
            },
            features: {
              name: 'Features',
              features: [
                'Email notifications',
                'Basic integrations',
                'Custom success page',
                'Basic support',
                'HTTPS encryption',
              ],
            },
          },
        },
        {
          name: 'Plus',
          price: 129,
          categories: {
            formBuilding: {
              name: 'Form Building',
              features: [
                'Custom CSS & fonts',
                'Custom domains',
                'Custom JavaScript',
                'Scheduling field',
                'Payment collection',
                'File attachments',
                'Password protection',
                'Hidden fields',
                'Custom redirects',
              ],
            },
            usage: {
              name: 'Usage',
              features: [
                '500 responses/month',
                'White labeling',
                'Advanced analytics',
                'JSON exports',
              ],
            },
            integrations: {
              name: 'Integrations',
              features: [
                'CRM integration',
                'Webhook support',
                'Zapier integration',
                'Priority support',
                'API access',
              ],
            },
          },
        },
        {
          name: 'Pro',
          price: 189,
          categories: {
            analytics: {
              name: 'Analytics',
              features: [
                'A/B testing',
                'Drop-off rates',
                'Form recovery',
                'Partial submissions',
                'User behavior tracking',
                'Custom reports',
                'Export scheduling',
              ],
            },
            advancedFeatures: {
              name: 'Advanced Features',
              features: [
                'Unlimited responses',
                'Automations & workflows',
                'Proposals generator',
                'Address autocomplete',
                'KVK autofill',
                'Custom API access',
                'Dedicated support',
                'SLA guarantee',
                'SAML SSO',
                'Advanced security features',
              ],
            },
          },
        },
      ]);

      const displayTiers = computed(() => {
        return baseTiers.value.map((tier) => ({
          ...tier,
          displayPrice: isMonthly.value ? Math.round(tier.price * 1.2) : tier.price,
        }));
      });

      // Methods
      const fetchLicenses = async () => {
        try {
          loading.value = true;
          error.value = null;
          const data = await licenseService.getLicenses();
          licenses.value = data;
          
          // Debug: log license data
          console.log('📦 Fetched licenses:', data);
          if (data.base && data.base.length > 0) {
            console.log('📦 First base license:', data.base[0]);
            console.log('📦 First base license features:', data.base[0].features);
          }
        } catch (err) {
          console.error('Error fetching licenses:', err);
          error.value = err.response?.data?.error || 'Fout bij ophalen licenses';
        } finally {
          loading.value = false;
        }
      };

      const handleCheckoutForLicense = async (license) => {
        if (!license) {
          error.value = 'Geen license gevonden';
          return;
        }

        // Set selected license
        selectedLicenseId.value = license.id;
        selectedQuantity.value = license.min_users || 1;

        // Validate quantity is within range
        const minUsers = license.min_users || 1;
        const maxUsers = license.max_users || 10;
        if (selectedQuantity.value < minUsers || selectedQuantity.value > maxUsers) {
          error.value = `Aantal gebruikers moet tussen ${minUsers} en ${maxUsers} zijn`;
          uiStore.addModal(null, {
            title: 'Ongeldig aantal gebruikers',
            description: `Aantal gebruikers moet tussen ${minUsers} en ${maxUsers} zijn.`,
          });
          selectedLicenseId.value = null;
          return;
        }

        try {
          checkoutLoading.value = true;
          error.value = null;
          // Quantity is het aantal gebruikers, niet het aantal keer het plan
          const response = await licenseService.startCheckout(
            license.id,
            selectedQuantity.value, // Aantal gebruikers (1-5)
            []
          );

          // Redirect naar Stripe Checkout
          window.location.href = response.url;
        } catch (err) {
          selectedLicenseId.value = null;
          const errorCode = err.response?.data?.code;
          const errorMessage = err.response?.data?.error || err.message;

          if (errorCode === 'MANUAL_SUBSCRIPTION_EXISTS') {
            uiStore.addModal(null, {
              title: 'Handmatige Subscription',
              description: 'Je hebt een handmatige subscription. Neem contact op met support.',
            });
          } else if (errorCode === 'STRIPE_SUBSCRIPTION_EXISTS') {
            uiStore.addModal(null, {
              title: 'Subscription bestaat al',
              description: 'Je hebt al een subscription. Gebruik "Beheer Billing" om te wijzigen.',
            });
          } else {
            error.value = `Fout: ${errorMessage}`;
            uiStore.addModal(null, {
              title: 'Fout',
              description: errorMessage,
            });
          }
        } finally {
          checkoutLoading.value = false;
          selectedLicenseId.value = null;
        }
      };

      const handleCheckout = async () => {
        // Legacy method - use handleCheckoutForLicense instead
        if (baseLicense.value) {
          await handleCheckoutForLicense(baseLicense.value);
        }
      };

      const handleOpenPortal = async () => {
        try {
          portalLoading.value = true;
          error.value = null;
          const response = await licenseService.openPortal();
          window.location.href = response.url;
        } catch (err) {
          const errorMessage = err.response?.data?.error || 'Fout bij openen portal';
          error.value = errorMessage;
          uiStore.addModal(null, {
            title: 'Fout',
            description: errorMessage,
          });
        } finally {
          portalLoading.value = false;
        }
      };

      const handleSelectTier = (tier) => {
        // Scroll naar base license selection of toon checkout
        // Voor nu: scroll naar base license sectie
        const baseLicenseSection = document.querySelector('.base-license');
        if (baseLicenseSection) {
          baseLicenseSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        // Optioneel: auto-select aantal gebruikers op basis van tier
        // selectedQuantity.value = tier.recommendedUsers || 3;
      };

      const handleSelectLicense = (license) => {
        // Set selected license and scroll to checkout form
        // Find the base license section that matches this license
        const baseLicenseSection = document.querySelector('.base-license');
        if (baseLicenseSection) {
          baseLicenseSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        // Auto-select recommended quantity
        if (license.min_users) {
          selectedQuantity.value = license.min_users;
        }
      };

      const formatFeatureName = (featureName) => {
        // Convert feature names like "slim_verkopen" to "Slim Verkopen"
        return featureName
          .split('_')
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
      };

      const getLicenseFeatures = (license) => {
        // First try to get features from license object
        if (license.features && typeof license.features === 'object') {
          const features = [];
          Object.entries(license.features).forEach(([featureName, enabled]) => {
            if (enabled) {
              features.push(formatFeatureName(featureName));
            }
          });
          if (features.length > 0) {
            return features;
          }
        }
        
        // Fallback: hardcoded features based on license name/type
        const hardcodedFeatures = getHardcodedFeatures(license);
        return hardcodedFeatures;
      };

      const getHardcodedFeatures = (license) => {
        // Hardcoded features per license type/name
        const licenseName = license.name?.toLowerCase() || '';
        
        // Funnelflow Team / Smartflow Team - Starter tier features
        if (licenseName.includes('team') || licenseName.includes('funnelflow') || licenseName.includes('smartflow')) {
          return [
            // Form Building
            'Unlimited forms',
            'Multi-page forms',
            '50+ field types',
            'Basic field validation',
            'File upload fields',
            'Date & time fields',
            'Conditional logic',
            'Form templates',
            'Duplicate forms',
            // Usage
            '150 responses/month',
            'Unlimited users',
            'Basic form analytics',
            'CSV exports',
            // Features
            'Email notifications',
            'Basic integrations',
            'Custom success page',
            'Basic support',
            'HTTPS encryption',
          ];
        }
        
        // Default features if no match
        return [
          `Voor ${license.min_users || 1}-${license.max_users} gebruikers`,
          license.description || 'Basis licentie',
        ];
      };

      const scrollToLicenses = () => {
        // Scroll naar base license selection
        const licensesSection = document.querySelector('.base-license-selection') || 
                                document.querySelector('.pricing-plans-overview');
        if (licensesSection) {
          licensesSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      };

      const handleAddModule = async (moduleId) => {
        try {
          addModuleLoading.value = moduleId;
          error.value = null;
          // Use license ID, not subscription ID
          await licenseService.addModule(moduleId);
          // Refresh user data to get updated subscriptions
          await userStore.fetchUserData();
          await fetchLicenses();
          uiStore.addModal(null, {
            title: 'Succes',
            description: 'Module succesvol toegevoegd!',
          });
        } catch (err) {
          const errorMessage = err.response?.data?.error || 'Fout bij toevoegen module';
          error.value = errorMessage;
          uiStore.addModal(null, {
            title: 'Fout',
            description: errorMessage,
          });
        } finally {
          addModuleLoading.value = null;
        }
      };

      const isModuleActive = (moduleId) => {
        // Check if module is active by license ID or subscription ID
        if (userData.value?.license_subscriptions) {
          return userData.value.license_subscriptions.some(
            (sub) =>
              (sub.license?.id === moduleId || sub.id === moduleId) &&
              sub.license?.type === 'module' &&
              sub.status === 'active'
          );
        }
        // Fallback to active_modules array
        return activeModules.value.some((m) => m.id === moduleId);
      };

      const getModulePrice = (moduleId) => {
        // First try to get price from license_subscriptions
        if (userData.value?.license_subscriptions) {
          const subscription = userData.value.license_subscriptions.find(
            (sub) =>
              (sub.license?.id === moduleId || sub.id === moduleId) &&
              sub.license?.type === 'module'
          );
          if (subscription?.license?.price) {
            return subscription.license.price;
          }
        }
        // Fallback to available modules from API
        const module = availableModules.value.find((m) => m.id === moduleId);
        return module?.price || 249; // Default price if not found
      };

      const getModuleSubscriptionId = (moduleId) => {
        // Get subscription ID for module management
        if (userData.value?.license_subscriptions) {
          const subscription = userData.value.license_subscriptions.find(
            (sub) =>
              (sub.license?.id === moduleId || sub.id === moduleId) &&
              sub.license?.type === 'module'
          );
          return subscription?.id;
        }
        // Fallback: check active_modules array
        const module = activeModules.value.find((m) => m.id === moduleId);
        return module?.id;
      };

      // Lifecycle
      onMounted(async () => {
        // Debug logging
        console.log('🔍 Billing View Debug:');
        console.log('  - userData:', userData.value);
        console.log('  - userData keys:', userData.value ? Object.keys(userData.value) : 'null');
        console.log('  - hasBaseLicense:', hasBaseLicense.value);
        console.log('  - hasStripeSubscription:', hasStripeSubscription.value);
        console.log('  - showNewInterface:', showNewInterface.value);
        console.log('  - base_license in userData:', userData.value && 'base_license' in userData.value);
        console.log('  - base_license value:', userData.value?.base_license);
        console.log('  - license_subscriptions:', userData.value?.license_subscriptions);
        
        // Check for success query parameter
        if (route.query.success === 'true') {
          showSuccessMessage.value = true;
          // Refresh user data to get new subscription
          try {
            await userStore.fetchUserData();
          } catch (error) {
            console.error('Error refreshing user data after success:', error);
          }
          // Remove query parameter from URL
          router.replace({ query: {} });
          // Hide success message after 5 seconds
          setTimeout(() => {
            showSuccessMessage.value = false;
          }, 5000);
        }
        
        // Always fetch licenses, regardless of userData structure
        fetchLicenses();
      });

      return {
        // State
        loading,
        checkoutLoading,
        portalLoading,
        addModuleLoading,
        error,
        licenses,
        selectedQuantity,
        selectedLicenseId,
        isMonthly,
        showSuccessMessage,
        // Computed
        userData,
        hasBaseLicense,
        hasStripeSubscription,
        showNewInterface,
        activeModules,
        activeModuleSubscriptions,
        baseLicense,
        baseLicenseSubscription,
        availableModules,
        availableBaseLicenses,
        baseLicensePrice,
        totalMonthly,
        maxUsers,
        userRange,
        baseTiers,
        displayTiers,
        // Methods
        handleCheckout,
        handleCheckoutForLicense,
        handleOpenPortal,
        handleAddModule,
        handleSelectTier,
        handleSelectLicense,
        formatFeatureName,
        getLicenseFeatures,
        scrollToLicenses,
        isModuleActive,
        getModulePrice,
        getModuleSubscriptionId,
      };
    },
  };
</script>

<style scoped lang="scss">
  .pricing-view {
    padding: 40px 0;
  }

  // Error Banner
  .success-banner {
    background: #f0fdf4;
    border: 1px solid #86efac;
    color: #166534;
    padding: 1rem;
    border-radius: 4px;
    margin-bottom: 2rem;

    &__content {
      display: flex;
      align-items: center;
      gap: 8px;
    }
  }

  .error-banner {
    background: #fee;
    border: 1px solid #fcc;
    color: #c33;
    padding: 1rem;
    border-radius: 4px;
    margin-bottom: 2rem;
  }

  // Current Subscription
  .current-subscription {
    margin-bottom: 3rem;
  }

  .plan-card {
    background: #ffffff;
    border-radius: 0.5rem;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    overflow: hidden;
    border: 1px solid var(--slate-200);
    padding: 24px;

    &--active {
      border-color: var(--green-500);
    }

    &__header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 1.5rem;

      h3 {
        font-size: 24px;
        font-weight: 600;
        margin: 0;
      }
    }

    &__details {
      margin-bottom: 1.5rem;
    }

    &__price {
      font-size: 1.5rem;
      font-weight: bold;
      color: var(--slate-900);
      margin-bottom: 0.5rem;
    }

    &__users {
      color: var(--slate-600);
      margin-bottom: 1rem;
    }

    &__total {
      margin-top: 1rem;
      padding-top: 1rem;
      border-top: 1px solid var(--slate-200);
      font-size: 1.25rem;
      color: var(--slate-900);
    }

    &__actions {
      margin-top: 1.5rem;
    }
  }

  // Modules List
  .modules-list {
    margin: 1rem 0;

    h4 {
      font-size: 16px;
      font-weight: 600;
      margin-bottom: 0.75rem;
      color: var(--slate-700);
    }

    ul {
      list-style: none;
      padding: 0;
      margin: 0;
    }

    &__item {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 8px 0;
      color: var(--slate-600);

      strong {
        color: var(--slate-900);
      }
    }

    &__price {
      margin-left: auto;
      color: var(--slate-500);
    }
  }

  // Base License
  .base-license {
    margin-bottom: 3rem;

    h2 {
      font-size: 20px;
      font-weight: 600;
      margin-bottom: 1.5rem;
      color: var(--slate-900);
    }
  }

  .license-card {
    background: #ffffff;
    border: 1px solid var(--slate-200);
    border-radius: 0.5rem;
    padding: 24px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);

    &__header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 1rem;

      h3 {
        font-size: 24px;
        font-weight: 600;
        margin: 0;
      }
    }

    &__price {
      font-size: 1.5rem;
      font-weight: bold;
      color: var(--slate-900);
    }

    &__description {
      color: var(--slate-600);
      margin: 0.5rem 0;
    }

    &__users {
      color: var(--slate-600);
      margin-bottom: 1.5rem;
    }
  }

  // Checkout Form
  .checkout-form {
    margin-top: 1.5rem;

    &__label {
      display: block;
      font-weight: 500;
      margin-bottom: 0.5rem;
      color: var(--slate-700);
    }

    &__select {
      width: 100%;
      padding: 0.5rem;
      border: 1px solid var(--slate-200);
      border-radius: 4px;
      margin-bottom: 0.75rem;
      font-size: 14px;
    }

    &__info {
      font-size: 13px;
      color: var(--slate-500);
      margin-bottom: 1rem;
      font-style: italic;
    }

    &__button {
      width: 100%;
    }
  }

  // Pricing Plans Overview
  .pricing-plans-overview {
    margin-top: 3rem;
    margin-bottom: 3rem;

    h2 {
      font-size: 20px;
      font-weight: 600;
      margin-bottom: 1.5rem;
      color: var(--slate-900);
    }

    .pricing-table {
      margin-top: 1.5rem;
    }
  }

  // Modules Section
  .modules-section {
    margin-top: 3rem;

    h2 {
      font-size: 20px;
      font-weight: 600;
      margin-bottom: 1.5rem;
      color: var(--slate-900);
    }
  }

  .loading-state {
    text-align: center;
    padding: 2rem;
    color: var(--slate-500);
  }

  .modules-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 1.5rem;
  }

  .module-card {
    background: #ffffff;
    border: 1px solid var(--slate-200);
    border-radius: 0.5rem;
    padding: 1.5rem;
    transition: all 0.2s;

    &--active {
      background: #f0f9ff;
      border-color: var(--green-500);
    }

    &__header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 1rem;

      h3 {
        font-size: 18px;
        font-weight: 600;
        margin: 0;
      }
    }

    &__price {
      font-size: 1.25rem;
      font-weight: bold;
      color: var(--slate-900);
      margin-bottom: 0.5rem;
    }

    &__description {
      color: var(--slate-600);
      margin: 0.5rem 0 1rem 0;
    }

    &__info {
      color: var(--slate-500);
      font-size: 14px;
      font-style: italic;
    }
  }

  // Badge
  .badge {
    padding: 0.25rem 0.75rem;
    border-radius: 12px;
    font-size: 0.875rem;
    font-weight: 500;

    &--active {
      background: var(--green-100);
      color: var(--green-700);
    }
  }

  // No Subscription
  .no-subscription {
    margin-bottom: 3rem;

    h2 {
      font-size: 20px;
      font-weight: 600;
      margin-bottom: 0.5rem;
      color: var(--slate-900);
    }

    p {
      color: var(--slate-600);
    }
  }

  // Pricing Table - Original styling
  .pricing-table {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 12px;
    margin-top: 40px;
    @include xl-down {
      grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    }

    &--grid {
      // For base licenses: show 3 columns side by side (same as original)
      grid-template-columns: repeat(3, 1fr);
      gap: 12px; // Same gap as original pricing table
      @include xl-down {
        grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
      }
      @include lg-down {
        grid-template-columns: 1fr;
      }
    }
  }

  .pricing-card {
    background: #ffffff;
    border-radius: 0.5rem;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    overflow: hidden;
    transition: transform 0.2s;
    border: 1px solid var(--slate-200);

    &:hover {
      transform: translateY(-4px);
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    }

    &__header {
      padding: 24px;
      text-align: center;
      background: var(--slate-50);
      border-bottom: 1px solid var(--slate-200);

      h3 {
        font-size: 24px;
        font-weight: 600;
        margin-bottom: 16px;
        color: var(--slate-900);
      }
    }

    &__price {
      font-size: 48px;
      font-weight: 700;
      line-height: 1;
      margin-bottom: 16px;
      color: var(--slate-900);

      .currency {
        font-size: 24px;
        vertical-align: super;
        margin-right: 2px;
      }

      .amount {
        display: inline-block;
      }

      .period {
        font-size: 16px;
        font-weight: 400;
        color: var(--slate-500);
        margin-left: 4px;
      }
    }

    &__users-info {
      font-size: 14px;
      color: var(--slate-500);
      margin-bottom: 16px;
    }

    &__duration {
      font-size: 14px;
      color: var(--slate-500);
      margin-bottom: 16px;
    }

    &__cta {
      width: 100%;
      margin-top: 16px;
    }

    &__features {
      padding: 24px;
    }
  }

  .feature-category {
    & + & {
      margin-top: 24px;
    }
    &__icon {
      display: inline-block;
      margin-right: 8px;
      color: var(--green-500);
      width: 1.25rem;
      height: 1.25rem;
      border-radius: 50%;
      background: var(--green-50);
      border: 1px solid var(--green-500);
      display: flex;
      align-items: center;
      justify-content: center;
    }

    h4 {
      font-size: 16px;
      font-weight: 600;
      margin-bottom: 12px;
      color: var(--slate-700);
    }

    ul {
      list-style: none;
      padding: 0;
      margin: 0;
    }

    li {
      display: flex;
      align-items: center;
      font-size: 14px;
      color: var(--slate-600);
      padding: 8px 0;

      i {
        color: var(--green-500);
        margin-right: 8px;
      }
    }

    // Support for feature-list and feature-item (same styling)
    .feature-list {
      list-style: none;
      padding: 0;
      margin: 0;
    }

    .feature-item {
      display: flex;
      align-items: center;
      font-size: 14px;
      color: var(--slate-600);
      padding: 8px 0;

      span {
        flex: 1;
      }
    }
  }

  .billing-toggle {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    margin-bottom: 32px;

    span {
      font-size: 14px;
      color: var(--slate-500);

      &.active {
        color: var(--slate-900);
        font-weight: 500;
      }
    }

    .savings-badge {
      background: var(--green-100);
      color: var(--green-700);
      padding: 4px 8px;
      border-radius: 12px;
      font-size: 12px;
      font-weight: 500;
      margin-left: 8px;
    }
  }
</style>
